import React, { useState, useEffect } from 'react';
import { superAdminAPI } from '../services/api';
import { useToast } from '../hooks/useToast';
import Icon from './Icon';

function SuperAdminMarketingCampaigns({ darkMode = true }) {
  const toast = useToast();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState(null);
  const [sendProgress, setSendProgress] = useState(null);
  const [setupResendLoading, setSetupResendLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_body: '',
    recipient_emails_text: '',
    phone_number: '',
    email_address: ''
  });
  const [templateLoaded, setTemplateLoaded] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Poll send-status when a campaign is sending
  useEffect(() => {
    if (!sendingCampaignId) return;
    const iv = setInterval(async () => {
      try {
        const res = await superAdminAPI.getMarketingCampaignSendStatus(sendingCampaignId);
        setSendProgress(res.data);
        if (res.data.status !== 'sending') {
          setSendingCampaignId(null);
          setSending(false);
          fetchCampaigns();
        }
      } catch (err) {
        setSendingCampaignId(null);
        setSending(false);
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [sendingCampaignId]);

  useEffect(() => {
    if (showCreateModal && !templateLoaded) {
      superAdminAPI.getMarketingTemplate()
        .then(res => {
          const t = res.data;
          setFormData(prev => ({
            ...prev,
            name: prev.name || 'Frame-Book intro campaign',
            subject: t.subject || 'Frame-Book – service history for bike workshops and riders',
            html_body: t.html_body || ''
          }));
          setTemplateLoaded(true);
        })
        .catch(() => {
          setTemplateLoaded(true);
          toast.error('Could not load template');
        });
    }
    if (!showCreateModal) setTemplateLoaded(false);
  }, [showCreateModal, templateLoaded]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getMarketingCampaigns();
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewHtml = () => {
    if (!formData.html_body) return '';
    return formData.html_body
      .replace(/\[Phone Number\]/g, formData.phone_number || '[Phone Number]')
      .replace(/\[Email Address\]/g, formData.email_address || '[Email Address]');
  };

  const parseEmails = (text) => {
    if (!text) return [];
    return text
      .split(/[\s,\n;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const emails = parseEmails(formData.recipient_emails_text);
    if (emails.length === 0) {
      toast.error('Add at least one valid email address');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    const finalHtml = (formData.html_body || '')
      .replace(/\[Phone Number\]/g, formData.phone_number || '[Phone Number]')
      .replace(/\[Email Address\]/g, formData.email_address || '[Email Address]');
    if (!finalHtml.trim()) {
      toast.error('Email content is required');
      return;
    }
    try {
      await superAdminAPI.createMarketingCampaign({
        name: formData.name || formData.subject.slice(0, 50),
        subject: formData.subject,
        html_body: finalHtml,
        recipient_emails: emails
      });
      toast.success('Campaign created');
      setShowCreateModal(false);
      setFormData({ name: '', subject: '', html_body: '', recipient_emails_text: '', phone_number: '', email_address: '' });
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await superAdminAPI.deleteMarketingCampaign(id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSend = async (campaign) => {
    const isResume = campaign.status === 'sending' || campaign.status === 'failed';
    const msg = isResume
      ? `Continue sending to remaining recipients? Already sent: ${campaign.sent_count || 0} of ${campaign.recipients_count}. Recipients already sent will be skipped (no duplicates).`
      : `Send to ${campaign.recipients_count} recipients?`;
    if (!window.confirm(msg)) return;
    try {
      setSending(true);
      setSendingCampaignId(campaign.id);
      setSendProgress({ sent_count: campaign.sent_count || 0, recipients_count: campaign.recipients_count });
      let res;
      do {
        res = await superAdminAPI.sendMarketingCampaign(campaign.id);
        setSendProgress({
          sent_count: res.data.total_sent,
          failed_count: res.data.total_failed,
          recipients_count: campaign.recipients_count,
          remaining: res.data.remaining,
          done: res.data.done,
          limit_reached: res.data.limit_reached,
          progress_pct: res.data.remaining === 0 ? 100 : Math.round(100 * res.data.total_sent / campaign.recipients_count)
        });
        if (res.data.limit_reached) {
          toast.info(res.data.message || `${res.data.sent_this_batch} sent (daily limit). Resume tomorrow for the rest.`);
          break;
        }
        if (!res.data.done) {
          await new Promise(r => setTimeout(r, 300));
        }
      } while (!res.data.done);
      if (res.data.done) {
        toast.success(`Campaign sent: ${res.data.total_sent} delivered, ${res.data.total_failed} failed`);
      }
      setSendingCampaignId(null);
      setSending(false);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
      setSendingCampaignId(null);
      setSending(false);
      fetchCampaigns();
    }
  };

  const loadAnalytics = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const res = await superAdminAPI.getMarketingCampaignAnalytics(campaign.id);
      setAnalytics(res.data);
      setShowAnalyticsModal(true);
    } catch (err) {
      toast.error('Failed to load analytics');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: { bg: '#6b7280', text: 'white' },
      sending: { bg: '#f59e0b', text: 'white' },
      sent: { bg: '#1A2811', text: 'white' },
      failed: { bg: '#ef4444', text: 'white' }
    };
    const c = colors[status] || colors.draft;
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: c.bg,
        color: c.text
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '-');

  return (
    <div className="super-admin-marketing-campaigns">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0, color: darkMode ? '#fff' : '#1a1a2e' }}>Marketing Email</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-secondary"
            onClick={async () => {
              setSetupResendLoading(true);
              try {
                const res = await superAdminAPI.setupResendAnalytics();
                const r = res.data?.results || {};
                toast.success(r.errors?.length ? `Setup done with notes: ${r.domain_tracking || ''} ${r.webhook || ''}` : 'Resend analytics configured!');
                if (r.errors?.length) toast.error(r.errors.join(' '));
              } catch (err) {
                toast.error(err.response?.data?.error || 'Setup failed');
              } finally {
                setSetupResendLoading(false);
              }
            }}
            disabled={setupResendLoading}
          >
            {setupResendLoading ? 'Setting up…' : 'Setup Resend Analytics'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + New Campaign
          </button>
        </div>
      </div>

      <p style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : '#666', marginBottom: '20px', fontSize: '14px' }}>
        Bulk email campaigns with custom recipient lists. Click <strong>Setup Resend Analytics</strong> to auto-configure webhook and tracking via API.
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : campaigns.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: darkMode ? '#1a1a2e' : '#f8f9fa',
          borderRadius: '12px'
        }}>
          <Icon name="email" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', color: darkMode ? '#fff' : '#1a1a2e' }}>No campaigns yet</h3>
          <p style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Create a campaign to send marketing emails to your list.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ backgroundColor: darkMode ? '#16213e' : '#f3f4f6' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Name / Subject</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Recipients</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Clicks</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Sent</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', color: darkMode ? '#a5b4fc' : '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}` }}>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#fff' : '#1a1a2e' }}>
                    <strong>{c.name || c.subject}</strong>
                    <div style={{ fontSize: '12px', color: darkMode ? '#a5b4fc' : '#666' }}>{c.subject}</div>
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666' }}>
                    {c.recipients_count}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {getStatusBadge(c.status)}
                    {c.status === 'sending' && sendingCampaignId === c.id && sendProgress && (
                      <div style={{ fontSize: '11px', marginTop: '4px', color: darkMode ? '#a5b4fc' : '#666' }}>
                        {sendProgress.sent_count} / {sendProgress.recipients_count} ({sendProgress.progress_pct ?? 0}%)
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666' }}>{c.clicks_count ?? 0}</td>
                  <td style={{ padding: '12px 16px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>
                    {c.sent_count > 0 ? `${c.sent_count} / ${c.recipients_count}` : '-'}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {c.status === 'draft' && (
                        <>
                          <button className="btn btn-sm btn-primary" onClick={() => handleSend(c)} disabled={sending || c.recipients_count === 0}>
                            {sending && sendingCampaignId === c.id ? 'Sending…' : 'Send'}
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>Delete</button>
                        </>
                      )}
                      {(c.status === 'sending' || c.status === 'failed') && (
                        <>
                          <button className="btn btn-sm btn-secondary" onClick={() => loadAnalytics(c)}>
                            Analytics
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={async () => {
                              try {
                                const res = await superAdminAPI.getMarketingCampaignResendCount(c.id);
                                const n = res.data.resend_actual_sent;
                                const rem = res.data.remaining;
                                toast.success(
                                  n != null
                                    ? `Resend: ${n} emails sent (of ${c.recipients_count}). ${rem > 0 ? rem + ' remaining – use Resume.' : 'All sent.'}`
                                    : res.data.suggestion || 'Check Resend dashboard for details.'
                                );
                              } catch (err) {
                                toast.error(err.response?.data?.error || 'Could not fetch Resend count');
                              }
                            }}
                          >
                            Check Resend
                          </button>
                          {(c.sent_count || 0) < (c.recipients_count || 0) && (
                            <button className="btn btn-sm btn-primary" onClick={() => handleSend(c)} disabled={sending}>
                              {sending && sendingCampaignId === c.id ? 'Sending…' : 'Resume'}
                            </button>
                          )}
                        </>
                      )}
                      {c.status === 'sent' && (
                        <button className="btn btn-sm btn-secondary" onClick={() => loadAnalytics(c)}>
                          Analytics
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal – full-page layout */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            overflow: 'hidden'
          }}
          onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              margin: 0,
              minHeight: 0,
              width: '100%',
              backgroundColor: darkMode ? '#1a1a2e' : '#fff',
              overflow: 'hidden'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`
            }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: darkMode ? '#fff' : '#1a1a2e' }}>
                New Marketing Campaign
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: darkMode ? '#a5b4fc' : '#666',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Two-column: form + preview */}
            <form onSubmit={handleCreate} style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
              <div style={{
                flex: '0 0 380px',
                padding: '24px',
                overflowY: 'auto',
                borderRight: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>Campaign name (optional)</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. UK workshops intro"
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: '8px',
                      border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                      backgroundColor: darkMode ? '#16213e' : '#fff', color: darkMode ? '#fff' : '#1a1a2e'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>Subject *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                    required
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: '8px',
                      border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                      backgroundColor: darkMode ? '#16213e' : '#fff', color: darkMode ? '#fff' : '#1a1a2e'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>Phone number</label>
                  <input
                    type="text"
                    value={formData.phone_number}
                    onChange={e => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="+44 123 456 7890"
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: '8px',
                      border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                      backgroundColor: darkMode ? '#16213e' : '#fff', color: darkMode ? '#fff' : '#1a1a2e'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>Email address</label>
                  <input
                    type="text"
                    value={formData.email_address}
                    onChange={e => setFormData({ ...formData, email_address: e.target.value })}
                    placeholder="contact@frame-book.com"
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: '8px',
                      border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                      backgroundColor: darkMode ? '#16213e' : '#fff', color: darkMode ? '#fff' : '#1a1a2e'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px' }}>Recipient emails *</label>
                  <textarea
                    value={formData.recipient_emails_text}
                    onChange={e => setFormData({ ...formData, recipient_emails_text: e.target.value })}
                    rows={6}
                    placeholder="Paste email addresses (comma, newline, or semicolon separated)"
                    style={{
                      width: '100%', padding: '12px 14px', borderRadius: '8px',
                      border: `1px solid ${darkMode ? '#4a5568' : '#e5e7eb'}`,
                      backgroundColor: darkMode ? '#16213e' : '#fff', color: darkMode ? '#fff' : '#1a1a2e',
                      resize: 'vertical', fontFamily: 'monospace', fontSize: '13px'
                    }}
                  />
                  <small style={{ display: 'block', marginTop: '6px', color: darkMode ? '#6b7280' : '#9ca3af', fontSize: '12px' }}>
                    {parseEmails(formData.recipient_emails_text).length} valid emails
                  </small>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create Campaign
                  </button>
                </div>
              </div>

              {/* Preview – full height */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '16px 24px',
                  borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`,
                  color: darkMode ? '#a5b4fc' : '#666',
                  fontSize: '14px',
                  fontWeight: 600
                }}>
                  Előnézet – így fog kinézni a kiküldött e-mail
                </div>
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '24px',
                  backgroundColor: darkMode ? '#0f0f1a' : '#f8fafc',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '100%',
                    maxWidth: '600px',
                    background: '#fff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                  }}>
                    <div style={{
                      background: '#f8fafc',
                      padding: '16px 20px',
                      borderBottom: '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#64748b'
                    }}>
                      <div><strong>From:</strong> Frame-Book</div>
                      <div style={{ marginTop: '6px' }}><strong>Subject:</strong> {formData.subject || 'Frame-Book – service history for bike workshops and riders'}</div>
                    </div>
                    <div
                      style={{
                        padding: '28px 24px',
                        minHeight: '400px',
                        fontSize: '16px',
                        lineHeight: 1.6,
                        color: '#1a1a1a'
                      }}
                      dangerouslySetInnerHTML={{ __html: getPreviewHtml() || '<p style="color:#94a3b8;margin:0">Betöltés…</p>' }}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedCampaign && analytics && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: darkMode ? '#1a1a2e' : '#fff',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px', color: darkMode ? '#fff' : '#1a1a2e' }}>Campaign Analytics</h3>
            <p style={{ color: darkMode ? '#a5b4fc' : '#666', fontSize: '13px', marginBottom: '20px' }}>
              Per-recipient: who received (Sent) and who clicked.
              {analytics.from_resend && (
                <span style={{ display: 'block', marginTop: '6px', color: '#f59e0b' }}>
                  Sent data from Resend (campaign was interrupted – shows who actually received before it stopped).
                </span>
              )}
            </p>
            {(analytics.clicks_count === 0 && analytics.sent_count > 0) && (
              <div style={{
                padding: '12px 16px',
                background: darkMode ? 'rgba(251,191,36,0.12)' : 'rgba(251,191,36,0.2)',
                border: `1px solid ${darkMode ? 'rgba(251,191,36,0.3)' : '#f59e0b'}`,
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '13px',
                color: darkMode ? '#fbbf24' : '#92400e'
              }}>
                <strong>No clicks yet?</strong> Recipients who click the link in the email are tracked via our unique per-recipient links. Resend webhook is optional.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
              <div style={{ padding: '16px', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4A8C18' }}>{analytics.sent_count}</div>
                <div style={{ fontSize: '12px', color: darkMode ? '#a5b4fc' : '#666' }}>Delivered</div>
              </div>
              <div style={{ padding: '16px', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{analytics.clicks_count}</div>
                <div style={{ fontSize: '12px', color: darkMode ? '#a5b4fc' : '#666' }}>Clicks ({analytics.click_rate}%)</div>
              </div>
              <div style={{ padding: '16px', background: darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>{analytics.failed_count}</div>
                <div style={{ fontSize: '12px', color: darkMode ? '#a5b4fc' : '#666' }}>Failed</div>
              </div>
            </div>
            {analytics.recipients_activity && analytics.recipients_activity.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Per-recipient activity</strong>
                <div style={{
                  maxHeight: '280px', overflow: 'auto', marginTop: '8px',
                  border: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`, borderRadius: '8px'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: darkMode ? '#16213e' : '#f3f4f6', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>Email</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', color: darkMode ? '#a5b4fc' : '#666' }}>Sent</th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', color: darkMode ? '#a5b4fc' : '#666' }}>Clicked</th>
                        <th style={{ padding: '10px 12px', textAlign: 'left', color: darkMode ? '#a5b4fc' : '#666' }}>When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recipients_activity.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}` }}>
                          <td style={{ padding: '8px 12px', color: darkMode ? '#fff' : '#1a1a2e' }}>{r.email}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {r.sent_at ? (
                              <span style={{ color: '#4A8C18', fontSize: '11px' }}>{new Date(r.sent_at).toLocaleString()}</span>
                            ) : (
                              <span style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {r.clicked ? (
                              <span style={{ color: '#f59e0b', fontWeight: 600 }}>Yes</span>
                            ) : (
                              <span style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', color: darkMode ? '#a5b4fc' : '#666', fontSize: '12px' }}>
                            {r.clicked_at ? `Clicked ${new Date(r.clicked_at).toLocaleString()}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {analytics.events && analytics.events.length > 0 && (
              <div>
                <strong style={{ color: darkMode ? '#a5b4fc' : '#666' }}>Recent events</strong>
                <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '8px' }}>
                  {analytics.events.slice(0, 15).map((ev, i) => (
                    <div key={i} style={{
                      padding: '6px 0', borderBottom: `1px solid ${darkMode ? '#2d3748' : '#e5e7eb'}`,
                      fontSize: '12px', color: darkMode ? '#fff' : '#1a1a2e'
                    }}>
                      <span style={{
                        padding: '2px 6px', borderRadius: '4px', marginRight: '8px',
                        background: ev.event_type === 'clicked' ? '#f59e0b' : '#3b82f6', color: 'white', fontSize: '10px'
                      }}>{ev.event_type}</span>
                      {ev.recipient_email} {ev.clicked_url && `→ ${ev.clicked_url}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button className="btn btn-secondary" style={{ marginTop: '20px' }} onClick={() => { setShowAnalyticsModal(false); setSelectedCampaign(null); setAnalytics(null); }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminMarketingCampaigns;
